import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { visuallyHidden } from '@mui/utils';
import { renderRich } from './richText';
import { LIGHT_GRAY } from '../../common/colors';
import { limitText, limitValues, requestLimitsStart, SIGNED_OUT_RATE } from '../../common/pricing';

// "Compare plans" under the /pricing cards (ocl_online#299). Collapsed by
// default and in small print, so the page leads with what each plan is for,
// not with a grid of quotas. The first table shows availability, with the
// words behind the marks for screen readers; the second, each plan's
// allowances and limits (ocl_online#448).
const Mark = ({ mark, row, t }) => {
  if (mark === 'yes')
    return (
      <>
        <CheckIcon aria-hidden sx={{ fontSize: 18, color: 'primary.main', verticalAlign: 'middle' }} />
        <Box component="span" sx={visuallyHidden}>{t('pricing.compare.included')}</Box>
      </>
    );
  if (mark === 'preview')
    return t('pricing.compare.preview');
  if (mark === 'advanced')
    return t(`pricing.compare.advanced.${row}`);
  return (
    <>
      <span aria-hidden>—</span>
      <Box component="span" sx={visuallyHidden}>{t('pricing.compare.notIncluded')}</Box>
    </>
  );
};

const cellSx = { fontSize: '0.8125rem', color: 'surface.contrastText', borderColor: LIGHT_GRAY, py: 1 };

// On a phone the plan columns scroll sideways; the feature column stays put
// (sticky, opaque) so each row keeps its label.
const featureSx = {
  ...cellSx,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: 'background.paper',
  width: { xs: 132, sm: 'auto' },
  minWidth: { xs: 132, sm: 'auto' },
};

// One table: a row per feature, a column per plan. `cell` renders a plan's cell.
const PlanTable = ({ rows, planKeys, featureLabel, cell, t, sx }) => (
  // position: relative makes this the containing block of the cells'
  // visually hidden labels (position: absolute), so they're clipped with the
  // table instead of widening the page on a phone.
  <TableContainer sx={{ overflowX: 'auto', position: 'relative' }}>
    <Table size="small" sx={sx}>
      <TableHead>
        <TableRow>
          <TableCell sx={featureSx}>
            <Box component="span" sx={visuallyHidden}>{featureLabel}</Box>
          </TableCell>
          {planKeys.map(key => (
            <TableCell key={key} align="center" sx={{ ...cellSx, fontWeight: 600, color: 'surface.dark' }}>
              {t(`pricing.plans.${key}.name`)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => (
          <TableRow key={row.key}>
            <TableCell component="th" scope="row" sx={featureSx}>
              {row.label}
            </TableCell>
            {planKeys.map(plan => (
              <TableCell key={plan} align="center" sx={cellSx}>
                {cell(row, plan)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const Note = ({ children, first }) => (
  <Typography variant="caption" component="p" sx={{ color: 'surface.contrastText', mt: first ? 2 : 0.5 }}>
    {children}
  </Typography>
);

const PlanComparison = ({ rows, planKeys, limitRows, limitPlanKeys, t, lng }) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{ mt: 4, border: `1px solid ${LIGHT_GRAY}`, borderRadius: '12px', 'border-bottom-left-radius': '12px !important', 'border-bottom-right-radius': '12px !important', backgroundColor: 'background.paper', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} id="compare-plans-header" aria-controls="compare-plans-content">
      <Typography sx={{ fontWeight: 600, color: 'surface.dark' }}>
        {t('pricing.compare.heading')}
      </Typography>
    </AccordionSummary>
    {/* MUI gives the details' region its id and aria-labelledby from the
        summary's aria-controls and id; repeating them here duplicated both. */}
    <AccordionDetails sx={{ pt: 0 }}>
      <PlanTable
        rows={rows.map(row => ({ ...row, label: t(`pricing.compare.rows.${row.key}`) }))}
        planKeys={planKeys}
        featureLabel={t('pricing.compare.feature')}
        cell={({ key, marks, described }, plan) => (described ? t(`pricing.compare.${key}.${plan}`) : <Mark mark={marks[plan]} row={key} t={t} />)}
        t={t}
        sx={{ minWidth: 520 }}
      />
      <Note first>{renderRich(t('pricing.compare.previewNote'))}</Note>
      <Note>{renderRich(t('pricing.compare.enterpriseNote'))}</Note>

      <Typography component="h3" sx={{ fontWeight: 600, color: 'surface.dark', mt: 4, mb: 1 }}>
        {t('pricing.limits.heading')}
      </Typography>
      <PlanTable
        rows={limitRows.map(row => ({ ...row, label: t(`pricing.limits.rows.${row.key}`) }))}
        planKeys={limitPlanKeys}
        featureLabel={t('pricing.limits.feature')}
        cell={({ cells }, plan) => (cells[plan] ? limitText(cells[plan], t, lng) : <Mark mark={null} t={t} />)}
        t={t}
        // Tighter cells on a phone, so two plans fit without scrolling sideways.
        sx={{ '& .MuiTableCell-root': { px: { xs: 1, sm: 2 } } }}
      />
      <Note first>{renderRich(t('pricing.limits.tokensNote'))}</Note>
      <Note>{renderRich(t('pricing.limits.requestsNote', { ...limitValues(SIGNED_OUT_RATE, lng), date: requestLimitsStart(lng) }))}</Note>
      <Note>{renderRich(t('pricing.limits.teamNote'))}</Note>
      <Note>{t('pricing.limits.footnote')}</Note>
    </AccordionDetails>
  </Accordion>
);

export default PlanComparison;
